import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import AdminServices from '@Services/Admin.services.ts';
import { Admin } from '@Types/Admin.types';
import { EditAdminFields, MultiDropOption } from '@Types/Form.types';
import { KGOffice } from '@Types/Office.types';
import { DROPDOWN_DEFAULT, DROPDOWN_DEFAULT_NUMBER } from '@Utils/constants';
import { formatLocations } from '@Utils/format.utils.tsx';
import { handleRequestError } from '@Utils/request.utils';
import { useApolloClient } from '@apollo/client';
import { ErrorDisplay, MultiSelectDropdown, Toast } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GetOfficesDocument, GetOfficesQuery } from '~/__generated__/graph';

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  admin?: Admin;
};

const EditAdminModal = ({ show, onClose, onSuccess, admin }: Props) => {
  const client = useApolloClient();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [overseenLocations, setOverseenLocations] = useState<MultiDropOption<number>[]>([]);
  const [officesDropdown, setOfficesDropdown] = useState<MultiDropOption<number>[]>([]);
  const [offices, setOffices] = useState<KGOffice[]>([]);

  const { handleSubmit } = useForm<EditAdminFields>({
    defaultValues: {
      roleId: DROPDOWN_DEFAULT.value,
    },
  });

  const handleEditAdmin = (fields: EditAdminFields) => {
    const mappedLocations = offices.filter((location) => overseenLocations.some((v) => v.value === location.OfficeID));
    AdminServices.editAdmin(admin?.id, fields.roleId, mappedLocations)
      .then(() => {
        Toast.success('Admin updated successfully.');
        onSuccess();
        onClose();
      })
      .catch((errorResponse) => handleRequestError(errorResponse, setErrorMessage, true));
  };

  const getOffices = async () => {
    try {
      const { data } = await client.query<GetOfficesQuery>({
        query: GetOfficesDocument,
      });

      if (data && data.officeListV2) {
        setOffices(data.officeListV2);
        const mappedOffices: MultiDropOption<number>[] = data.officeListV2.map((office: KGOffice) => ({
          label: office.Name!,
          value: office.OfficeID,
        }));
        setOfficesDropdown(mappedOffices);
        setErrorMessage(undefined);
      }
    } catch {
      setErrorMessage('Error loading offices');
    }
  };

  useEffect(() => {
    getOffices();
  }, []);

  useEffect(() => {
    setOverseenLocations(formatLocations(admin?.overseenLocations));
  }, [show]);
  return (
    <div className='modal--fit-content modal--w-30vw modal--pt-20vh'>
      <ModalWrapper
        title='Edit Admin'
        show={show}
        handleClose={onClose}
        primaryButtonText='Confirm'
        primaryAction={() => handleSubmit(handleEditAdmin)()}>
        {errorMessage ? (
          <ErrorDisplay inline details={errorMessage} />
        ) : (
          <form onSubmit={() => handleSubmit(handleEditAdmin)()}>
            <div className='grid'>
              <div className='col-12'>
                <p>
                  <b>Name</b> : {admin?.name}
                </p>
                <p>
                  <b>Email</b> : {admin?.email}
                </p>
                <MultiSelectDropdown<number>
                  options={officesDropdown}
                  values={overseenLocations}
                  selectOption={(selected) => {
                    setOverseenLocations((locations) => [...locations, selected]);
                  }}
                  removeOption={(removed) =>
                    setOverseenLocations((locations) =>
                      locations.filter((location) => location.value !== removed.value),
                    )
                  }
                  fullWidth
                  label='Overseen Locations'
                  // neccesary to reset the default value, otherwise it shows the last selected value even with an empty values array
                  value={DROPDOWN_DEFAULT_NUMBER}
                />
                <br />
              </div>
            </div>
          </form>
        )}
      </ModalWrapper>
    </div>
  );
};

export default EditAdminModal;
